
import React from 'react';
import { Settings, Headphones, Wrench, Shield, Truck, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const WhyChooseUsSection = () => {
  const reasons = [
    {
      icon: <Settings className="h-10 w-10" />,
      title: 'Customization as a Service',
      description: 'Every component in your device is selected to match your exact requirements. We build technology that works the way you work.'
    },
    {
      icon: <Headphones className="h-10 w-10" />,
      title: 'Lifetime Technical Support',
      description: 'Our relationship doesn\'t end after delivery. Enjoy lifelong access to our technical experts for any assistance you may need.'
    },
    {
      icon: <Truck className="h-10 w-10" />,
      title: 'On-site Installation',
      description: 'For businesses in Mumbai, Delhi and Hyderabad, we offer professional on-site setup to ensure your systems run perfectly from day one.'
    },
    {
      icon: <Shield className="h-10 w-10" />,
      title: 'Comprehensive Warranty',
      description: 'All our products come with extended warranty options, giving you peace of mind about your technology investment.'
    },
    {
      icon: <Users className="h-10 w-10" />,
      title: 'Bulk Procurement',
      description: 'Special pricing and support packages for organizations requiring multiple units or regular hardware refreshes.'
    },
    {
      icon: <Wrench className="h-10 w-10" />,
      title: 'Client-First Design',
      description: 'We involve you at every step of the design process, ensuring the final product exceeds your expectations.'
    }
  ];
  
  return (
    <section id="why-choose-us" className="section-padding bg-tech-dark text-white relative overflow-hidden">
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-tech-dark to-tech-dark animate-gradient-flow"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,_#8b5cf610_1px,_transparent_1px),_linear-gradient(to_bottom,_#8b5cf610_1px,_transparent_1px)] bg-[size:20px_20px]"></div>
      </div>
      
      <div className="container mx-auto container-padding relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Why Choose <span className="text-accent animate-pulse">Truecore</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            With 25 years of experience and a commitment to excellence, we deliver technology 
            solutions that are reliable, innovative, and tailored to your specific needs.
          </p>
        </div>
        
        {/* Enhanced Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <Card 
              key={index} 
              className="group bg-tech-dark/50 backdrop-blur-sm border border-accent/20 hover:border-accent/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(100,255,218,0.2)] overflow-hidden"
            >
              <CardContent className="p-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center text-accent group-hover:scale-110 transition-transform duration-300">
                    {reason.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-center group-hover:text-accent transition-colors">{reason.title}</h3>
                <p className="text-gray-300 text-center">{reason.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Enhanced Stats Section */}
        <div className="mt-16 p-8 bg-gradient-to-br from-white/5 to-primary/5 backdrop-blur-sm rounded-xl border border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCard value="25+" label="Years of Experience" />
            <StatCard value="2000+" label="Clients Served" />
            <StatCard value="3" label="Tech Centers" />
            <StatCard value="100+" label="Tech Experts" />
          </div>
        </div>
        
        {/* Enhanced Process Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8 text-center">Our Process</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <ProcessStep 
              number="01" 
              title="Consultation" 
              description="We start by understanding your needs, challenges, and goals." 
            />
            <ProcessStep 
              number="02" 
              title="Design" 
              description="Our engineers craft a custom hardware solution based on your requirements." 
            />
            <ProcessStep 
              number="03" 
              title="Build" 
              description="We assemble your technology using top-quality components and rigorous testing." 
            />
            <ProcessStep 
              number="04" 
              title="Support" 
              description="After delivery, we provide ongoing technical support and warranty services." 
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ value, label }: { value: string; label: string }) => {
  return (
    <div className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-accent mb-2">{value}</div>
      <p className="text-sm md:text-base text-gray-300">{label}</p>
    </div>
  );
};

const ProcessStep = ({ number, title, description }: { number: string; title: string; description: string }) => {
  return (
    <div className="relative">
      {/* Number */}
      <div className="text-4xl font-bold text-accent/30 mb-2">{number}</div>
      
      {/* Content */}
      <h4 className="text-xl font-bold mb-2">{title}</h4>
      <p className="text-gray-300">{description}</p>
      
      {/* Connector Line (except for the last item) */}
      <div className="hidden md:block absolute top-6 right-0 h-0.5 w-full bg-accent/20 -z-10"></div>
    </div>
  );
};

export default WhyChooseUsSection;
